import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X, SpellCheck, Wand2, Scissors, PenLine, ListPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODES = [
  { id: "draft", label: "Draft for me", icon: PenLine, hint: "from the field's purpose and your notes" },
  { id: "grammar", label: "Fix grammar & spelling", icon: SpellCheck },
  { id: "improve", label: "Improve wording", icon: Wand2 },
  { id: "shorten", label: "Make it shorter", icon: Scissors },
  { id: "expand", label: "Add helpful detail", icon: ListPlus },
];

// Reads the label the way a person sees it: <label for>, wrapping <label>, aria-label, or the placeholder.
export const fieldLabel = (el) => {
  if (!el) return "";
  const byFor = el.id ? document.querySelector(`label[for="${CSS.escape(el.id)}"]`) : null;
  const wrap = el.closest("label");
  const txt = (byFor || wrap)?.innerText?.split("\n").map((x) => x.trim()).filter(Boolean)[0];
  return (el.getAttribute("aria-label") || el.dataset.aiLabel || txt || el.placeholder || "").slice(0, 200);
};

// Floating "AI" affordance for any free-text field: draft, proofread, improve, shorten, expand — with a preview before anything is replaced.
export const AiAssist = ({ value = "", onApply, getLabel, hint = "", context = "", placeholder = "", className = "", size = "sm", testid = "ai-assist" }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [ask, setAsk] = useState("");
  const [result, setResult] = useState(null);
  const run = async (mode) => {
    setBusy(mode); setResult(null);
    try {
      const r = await axios.post(`${API}/ai/assist`, { mode, text: value || "", label: getLabel?.() || "", placeholder, hint, instruction: ask, context });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI help isn't available right now"); } finally { setBusy(""); }
  };
  const apply = () => { onApply(result.text); setOpen(false); setResult(null); setAsk(""); toast.success(result.mode === "grammar" ? "Corrections applied" : "Text updated"); };
  const empty = !(value || "").trim();
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); } }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} title="AI writing help — draft, proofread, improve" className={`inline-flex items-center gap-1 rounded-full border border-border bg-card/90 backdrop-blur px-2 ${size === "xs" ? "h-6 text-[10px]" : "h-7 text-[11px]"} text-muted-foreground hover:text-primary hover:border-primary transition-colors ${className}`}>
          <Sparkles className="h-3 w-3" /> AI
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-3 text-sm" data-testid={`${testid}-panel`}>
        {!result ? (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Writing help for this field. Nothing changes until you choose “Use this”.</div>
            <input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="Optional: tell the AI what you want (e.g. “warmer”, “mention my sister”)" className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`${testid}-ask`} />
            <ul className="grid gap-1">
              {MODES.map((m) => { const Icon = m.icon; const off = m.id !== "draft" && empty; return (
                <li key={m.id}><button type="button" disabled={Boolean(busy) || off} onClick={() => run(m.id)} data-testid={`${testid}-${m.id}`} className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent">
                  {busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <Icon className="h-3.5 w-3.5 text-primary" />}<span className="flex-1">{m.label}</span>{m.hint && <span className="text-[10px] text-muted-foreground">{m.hint}</span>}
                </button></li>); })}
            </ul>
          </div>
        ) : (
          <div className="space-y-2" data-testid={`${testid}-result`}>
            <div className="text-[10px] uppercase tracking-widest font-mono text-primary">{MODES.find((m) => m.id === result.mode)?.label}</div>
            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/30 p-2 text-sm" data-testid={`${testid}-text`}>{result.text}</div>
            {result.notes?.length > 0 && <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5" data-testid={`${testid}-notes`}>{result.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
            <div className="flex gap-1.5 justify-end">
              <button type="button" onClick={() => setResult(null)} className="inline-flex items-center gap-1 rounded-full border border-border px-3 h-8 text-xs hover:border-primary" data-testid={`${testid}-back`}><X className="h-3 w-3" /> Try another</button>
              <button type="button" onClick={apply} className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 h-8 text-xs hover:bg-primary/90" data-testid={`${testid}-use`}><Check className="h-3 w-3" /> Use this</button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
